import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { validateAllPrograms, validateCompleteTokenDeployment } from "@/lib/anchor/validation";
import { Connection } from "@solana/web3.js";

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

// Create connection for on-chain validation
function createConnection(): Connection {
  const rpcUrl = import.meta.env.VITE_SOLANA_RPC_URL || "https://api.devnet.solana.com";
  return new Connection(rpcUrl, "confirmed");
}

// Enhanced error handling for on-chain operations
export class OnChainError extends Error {
  constructor(message: string, public code: string, public details?: any) {
    super(message);
    this.name = "OnChainError";
  }
}

// Validate on-chain state before API operations
async function validateOnChainState(operation: string, coinId?: string, mintAddress?: string): Promise<void> {
  try {
    const connection = createConnection();

    // Always validate programs are deployed
    await validateAllPrograms(connection);

    // If mint address provided, validate token deployment
    if (mintAddress) {
      const validation = await validateCompleteTokenDeployment(connection, mintAddress);
      if (!validation.isValid) {
        throw new OnChainError(
          `Token validation failed: ${validation.errors.join(", ")}`,
          "TOKEN_VALIDATION_FAILED",
          { coinId, mintAddress, errors: validation.errors }
        );
      }
    }
  } catch (error) {
    if (error instanceof OnChainError) {
      throw error;
    }
    throw new OnChainError(
      `On-chain validation failed for ${operation}: ${error instanceof Error ? error.message : String(error)}`,
      "ONCHAIN_VALIDATION_ERROR",
      { operation, coinId, mintAddress, originalError: error }
    );
  }
}

// Helper function for retrying requests with exponential backoff
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 3
): Promise<Response> {
  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // If rate limited (429), wait and retry
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : Math.pow(2, attempt) * 1000; // exponential backoff

        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, waitTime));
          continue;
        }
      }

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries) {
        // Wait before retrying network errors
        const waitTime = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }

      throw lastError;
    }
  }

  throw lastError!;
}

export interface CreateTokenParams {
  name: string;
  ticker: string;
  description?: string;
  image?: string;
  creatorWallet: string;
  targetMarketCap?: number;
  isPremium?: boolean;
  mintAddress?: string; // For client-side deployments
}

export interface CreateTokenResult {
  success: boolean;
  coinId: string;
  mintAddress?: string;
  creatorAta?: string;
  signature?: string;
  explorerUrl?: string;
  error?: string;
}

export interface ExecuteTradeParams {
  coinId: string;
  tradeType: "buy" | "sell";
  amount: number;
  userWallet: string;
  txSignature: string;
  slippageTolerance?: number;
}

export interface ExecuteTradeResult {
  success: boolean;
  tradeId: string;
  tradeType: string;
  tokensOut: number;
  solOut: number;
  fee: number;
  newPrice: number;
  newMarketCap: number;
  txSignature?: string;
  explorerUrl?: string;
  error?: string;
}

export interface TokenInfo {
  onChain: boolean;
  coin: any;
  tokenConfig?: {
    mintAddress: string;
    creatorAta: string;
    totalSupply: string;
    decimals: number;
    createTx: string;
    createdAt: number;
  };
  mintInfo?: {
    address: string;
    decimals: number;
    supply: number;
    mintAuthority?: string;
    freezeAuthority?: string;
  };
  userBalance?: number;
  explorerUrl?: string;
}

export async function createToken(params: CreateTokenParams): Promise<CreateTokenResult> {
  const coinId = crypto.randomUUID();
  
  try {
    const { data: session } = await supabase.auth.getSession();
    
    const response = await fetchWithRetry(`${FUNCTIONS_URL}/create-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session?.session?.access_token || ""}`,
      },
      body: JSON.stringify({
        ...params,
        coinId,
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || "Failed to create token");
    }

    return result;
  } catch (error) {
    logger.error("Create token error", { error, params });
    throw error;
  }
}

export async function executeTrade(params: ExecuteTradeParams): Promise<ExecuteTradeResult> {
  try {
    // Validate on-chain state before executing trade
    await validateOnChainState("executeTrade", params.coinId);

    const { data: session } = await supabase.auth.getSession();

    const response = await fetchWithRetry(`${FUNCTIONS_URL}/execute-trade`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session?.session?.access_token || ""}`,
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (!response.ok) {
      // Enhanced error handling for specific on-chain errors
      if (response.status === 503) {
        throw new OnChainError(
          "On-chain programs not available. Contact administrator.",
          "PROGRAM_UNAVAILABLE",
          result
        );
      }
      if (response.status === 404) {
        throw new OnChainError(
          "Token not found on-chain. Strict on-chain enforcement required.",
          "TOKEN_NOT_FOUND",
          result
        );
      }
      throw new OnChainError(
        result.error || "Failed to execute trade",
        "TRADE_EXECUTION_FAILED",
        result
      );
    }

    return result;
  } catch (error) {
    if (error instanceof OnChainError) {
      throw error;
    }
    logger.error("Execute trade error", { error, params });
    throw new OnChainError(
      error instanceof Error ? error.message : "Trade execution failed",
      "TRADE_ERROR",
      { params, originalError: error }
    );
  }
}

export async function getTokenInfo(coinId: string, walletAddress?: string): Promise<TokenInfo> {
  try {
    // Validate on-chain state before getting token info
    await validateOnChainState("getTokenInfo", coinId);

    const params = new URLSearchParams({ coinId });
    if (walletAddress) {
      params.append("wallet", walletAddress);
    }

    const response = await fetchWithRetry(`${FUNCTIONS_URL}/get-token-info?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const result = await response.json();

    if (!response.ok) {
      // Enhanced error handling for on-chain issues
      if (response.status === 503) {
        throw new OnChainError(
          "On-chain programs not available. Contact administrator.",
          "PROGRAM_UNAVAILABLE",
          result
        );
      }
      if (response.status === 404) {
        throw new OnChainError(
          "Token not found on-chain. Strict on-chain enforcement required.",
          "TOKEN_NOT_FOUND",
          result
        );
      }
      throw new OnChainError(
        result.error || "Failed to get token info",
        "TOKEN_INFO_FAILED",
        result
      );
    }

    return result;
  } catch (error) {
    if (error instanceof OnChainError) {
      throw error;
    }
    logger.error("Get token info error", { error, coinId, walletAddress });
    throw new OnChainError(
      error instanceof Error ? error.message : "Failed to get token info",
      "TOKEN_INFO_ERROR",
      { coinId, walletAddress, originalError: error }
    );
  }
}

// Helper to format Solana explorer URLs
export function getSolscanUrl(signature: string, cluster: "devnet" | "mainnet-beta" = "devnet"): string {
  return `https://solscan.io/tx/${signature}?cluster=${cluster}`;
}

export function getExplorerUrl(signature: string, cluster: "devnet" | "mainnet-beta" = "devnet"): string {
  return `https://explorer.solana.com/tx/${signature}?cluster=${cluster}`;
}

export function getAddressExplorerUrl(address: string, cluster: "devnet" | "mainnet-beta" = "devnet"): string {
  return `https://explorer.solana.com/address/${address}?cluster=${cluster}`;
}

export async function getTransactionDetails(signature: string, cluster: "devnet" | "mainnet-beta" = "devnet") {
  try {
    const rpcUrl = import.meta.env.VITE_SOLANA_RPC_URL || `https://api.${cluster}.solana.com`;

    const response = await fetch(rpcUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "getTransaction",
        params: [
          signature,
          {
            encoding: "jsonParsed",
            commitment: "confirmed",
            maxSupportedTransactionVersion: 0,
          },
        ],
      }),
    });

    const result = await response.json();

    if (result.error) {
      throw new Error(result.error.message);
    }

    return result.result;
  } catch (error) {
    logger.error("Get transaction details error", { error, signature, cluster });
    throw error;
  }
}
